import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FingerScan,
  InfoCircle,
  KeySquare,
  Moon,
  SecurityUser,
  ShieldTick,
  Sun1,
} from 'iconsax-reactjs';
import { usernameSchema } from '@pipyar/core';
import {
  browserSupportsWebAuthn,
  loginWithPasskey,
  registerPasskey,
} from '@/lib/auth';
import { AUTH_STATUS_KEY, useAuthStatus } from '@/lib/useAuth';
import { useTheme } from '@/lib/useTheme';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Field } from '@/components/ui/Field';
import { Logo } from '@/components/Logo';
import { useI18n } from '@/lib/i18n';

/**
 * One screen, two modes.
 *
 * The server tells us which via `canRegister`, which is true only while no account
 * exists. So the very first visit shows "create your account" and every visit after
 * that shows "sign in" — the user never chooses between them and cannot land in the
 * wrong one.
 */
export function LoginPage() {
  const { data: status } = useAuthStatus();
  const queryClient = useQueryClient();
  const { theme, toggle } = useTheme();

  const [username, setUsername] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);

  const isFirstRun = status?.canRegister === true;

  useEffect(() => {
    setSupported(browserSupportsWebAuthn());
  }, []);

  const onAuthenticated = async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
  };

  const register = useMutation({
    mutationFn: () => registerPasskey(username.trim()),
    onSuccess: onAuthenticated,
  });

  const login = useMutation({
    mutationFn: loginWithPasskey,
    onSuccess: onAuthenticated,
  });

  const busy = register.isPending || login.isPending;
  const activeError = register.error ?? login.error;

  const handleRegister = () => {
    const parsed = usernameSchema.safeParse(username);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Invalid username.');
      return;
    }
    setValidationError(null);
    register.reset();
    register.mutate();
  };

  const handleLogin = () => {
    login.reset();
    login.mutate();
  };

  const { t } = useI18n();

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-10">
      {/* Ambient gold glow. Purely decorative, so hidden from assistive tech. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 start-1/2 size-[34rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-52 end-1/4 size-[26rem] rounded-full bg-bull/5 blur-[120px]" />
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={toggle}
        className="absolute top-4 end-4"
        aria-label={theme === 'dark' ? t('shell.switchToLight') : t('shell.switchToDark')}
      >
        {theme === 'dark' ? <Sun1 size={19} /> : <Moon size={19} />}
      </Button>

      <div className="w-full max-w-[26rem]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="h-8" />
          <p className="mt-3 text-sm text-muted-foreground">
            Gold &amp; forex analysis desk
          </p>
        </div>

        <div className="glass rounded-2xl p-6 sm:p-7">
          {/* ---------- unsupported browser ---------- */}
          {!supported ? (
            <div className="space-y-3 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-danger/10 text-danger">
                <InfoCircle size={24} variant="Bold" />
              </div>
              <h1 className="text-lg font-semibold">{t('login.noPasskeys')}</h1>
              <p className="text-sm text-muted-foreground">
                This browser does not support passkeys, or the page is not on a secure
                connection. Open Pipyar over <span className="tabular">https://</span>{' '}
                in a current version of Chrome, Safari, Edge or Firefox.
              </p>
            </div>
          ) : isFirstRun ? (
            /* ---------- first run: create the account ---------- */
            <>
              <header className="mb-6 space-y-1.5">
                <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-primary/12 text-primary">
                  <SecurityUser size={22} variant="Bold" />
                </div>
                <h1 className="text-xl font-semibold tracking-tight">
                  Set up your account
                </h1>
                <p className="text-sm text-muted-foreground">
                  Choose a username, then create a passkey. This happens once — after
                  this, the passkey is the only way in.
                </p>
              </header>

              <div className="space-y-5">
                <Field
                  htmlFor="username"
                  label={t('login.username')}
                  hint="Only used to label the passkey. 3–32 characters."
                  error={validationError}
                >
                  <Input
                    id="username"
                    name="username"
                    value={username}
                    invalid={validationError !== null}
                    autoComplete="username webauthn"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    placeholder={t('login.usernamePlaceholder')}
                    disabled={busy}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (validationError) setValidationError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === t('login.enter')) handleRegister();
                    }}
                  />
                </Field>

                <Button full size="lg" onClick={handleRegister} disabled={busy}>
                  <FingerScan size={20} variant="Bold" />
                  {register.isPending ? 'Waiting for your device…' : t('login.createPasskey')}
                </Button>
              </div>
            </>
          ) : (
            /* ---------- returning: sign in ---------- */
            <>
              <header className="mb-6 space-y-1.5">
                <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-primary/12 text-primary">
                  <KeySquare size={22} variant="Bold" />
                </div>
                <h1 className="text-xl font-semibold tracking-tight">{t('login.welcome')}</h1>
                <p className="text-sm text-muted-foreground">
                  Sign in with the passkey on this device. No username, no password.
                </p>
              </header>

              <Button full size="lg" onClick={handleLogin} disabled={busy}>
                <FingerScan size={20} variant="Bold" />
                {login.isPending ? 'Waiting for your device…' : t('login.signIn')}
              </Button>
            </>
          )}

          {activeError && (
            <div
              role="alert"
              className="mt-5 flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
            >
              <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
              <span>{activeError.message}</span>
            </div>
          )}
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <ShieldTick size={14} variant="Bold" className="text-bull" />
          Your passkey never leaves your device
        </p>
      </div>
    </div>
  );
}
